import { useLocalStorage } from "@vueuse/core";
import { watch } from "vue";
import { createI18n } from "vue-i18n";
import type { LocaleMessageValue } from "vue-i18n";

export const langCodeList = ["zh-CN", "zh-TW"] as const;
export type AppLocale = (typeof langCodeList)[number];
export const localeConfigKey = "laicui_wms-locale";
export const langList = [
  { value: "zh-CN", label: "简体中文" },
  { value: "zh-TW", label: "繁體中文" },
] as const;

export function normalizeLocale(value: unknown): AppLocale {
  const normalized = typeof value === "string" ? value.replaceAll("_", "-").toLowerCase() : "";
  return normalized === "zh-tw" ? "zh-TW" : "zh-CN";
}

type MessageDictionary = Record<string, LocaleMessageValue>;

const messages: Record<AppLocale, MessageDictionary> = { "zh-CN": {}, "zh-TW": {} };
const modules = import.meta.glob<MessageDictionary>("./langs/**/*.json", {
  eager: true,
  import: "default",
});

for (const [path, content] of Object.entries(modules)) {
  const match = path.match(/\/langs\/(zh-CN|zh-TW)\/([^/]+)\.json$/);
  if (match) {
    messages[normalizeLocale(match[1])][match[2]] = content;
  }
}

const preference = useLocalStorage(localeConfigKey, "zh-CN");

export const i18n = createI18n({
  legacy: false,
  locale: normalizeLocale(preference.value),
  fallbackLocale: "zh-CN",
  messages,
  globalInjection: true,
  missingWarn: import.meta.env.DEV,
  fallbackWarn: false,
});

export const { t } = i18n.global;

export function changeLocale(value: unknown) {
  i18n.global.locale.value = normalizeLocale(value);
}

// Preserve the service's historical language-header format (zh_CN / zh_TW).
export function getCurrentLocale() {
  return i18n.global.locale.value.replace("-", "_");
}

const stopPreferenceWatch = watch(
  preference,
  (value) => {
    changeLocale(value);
    preference.value = i18n.global.locale.value;
  },
  { immediate: true, flush: "sync" },
);
const stopLocaleWatch = watch(
  i18n.global.locale,
  (value) => {
    preference.value = value;
    if (typeof document !== "undefined") document.documentElement.lang = value;
  },
  { immediate: true, flush: "sync" },
);

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    stopPreferenceWatch();
    stopLocaleWatch();
  });
}

export default i18n;
